import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, X, Link as LinkIcon, Trash2, Upload, Loader2, Share2, Images, Type, Check, Pencil, ClipboardCopy, CalendarDays, AtSign, Archive } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { copyToClipboard } from '../lib/clipboard';
import { stickyColorFor, clientColorFor } from '../lib/stickyColors';

const STATUSES = [
  { id: 'concept',   label: 'concept',   dot: '#8e6ba3' },
  { id: 'draft',     label: 'draft',     dot: '#94a3b8' },
  { id: 'ready',     label: 'ready',     dot: '#f59e0b' },
  { id: 'scheduled', label: 'scheduled', dot: '#a78bfa' },
  { id: 'live',      label: 'live',      dot: '#10b981' },
];
const statusMap = Object.fromEntries(STATUSES.map((s) => [s.id, s]));

// per-platform status with fallback to the post's overall status (so legacy posts
// start with the same answer on every platform until it's changed manually)
const platformStatusOf = (post, pl) => (post.platform_status && post.platform_status[pl]) || post.status || 'concept';

// small status pill used on feed tiles (one per platform when a post spans 2+)
const TileStatusPill = ({ status, label, date, onSelect, onSetDate, testid }) => (
  <div className="inline-flex items-center h-5 rounded-full bg-white/95 text-slate-700 shadow ring-1 ring-black/5 overflow-hidden whitespace-nowrap">
    {/* left side → status picker */}
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          title="change status"
          className="inline-flex items-center gap-1 h-5 pl-1.5 pr-1 text-[9px] font-semibold hover:bg-slate-100"
          data-testid={testid}
        >
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: status.dot }} />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-32 p-1.5" onClick={(e) => e.stopPropagation()}>
        {STATUSES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onSelect(s.id)}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-slate-100 text-left"
            data-testid={`${testid}-option-${s.id}`}
          >
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: s.dot }} />
            {s.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
    {/* right side → date-only calendar */}
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          title="change live date"
          className="inline-flex items-center gap-0.5 h-5 pl-1 pr-1.5 text-[9px] font-semibold hover:bg-slate-100 border-l border-slate-200"
          data-testid={`${testid}-date`}
        >
          {date ? fmtShortDate(date) : <CalendarDays className="w-2.5 h-2.5 text-slate-400" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-2" onClick={(e) => e.stopPropagation()}>
        <DateInput
          value={date || ''}
          onChange={(v) => onSetDate?.(v)}
          onCommit={(v) => onSetDate?.(v)}
          className="h-8 text-xs"
          data-testid={`${testid}-date-input`}
        />
      </PopoverContent>
    </Popover>
  </div>
);

// Native <input type=date> stutters on year entry when controlled: each partial year
// ("0002") re-renders and resets the field. This stays uncontrolled while focused (so the
// browser keeps your digits) and only syncs the external value back in when not focused.
const DateInput = ({ value, onChange, onCommit, ...props }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) ref.current.value = value || '';
  }, [value]);
  return (
    <Input
      ref={ref}
      type="date"
      defaultValue={value || ''}
      onChange={(e) => onChange?.(e.target.value)}
      onBlur={(e) => onCommit?.(e.target.value)}
      {...props}
    />
  );
};

const PLATFORMS = { instagram: 'IG', tiktok: 'TT', ig_trial: 'Trial', linkedin: 'LI' };
const PLATFORM_BADGE = { instagram: 'IG', tiktok: 'TT', linkedin: 'LI', ig_trial: 'IGT' };
// Filter-row-only platform list for FEED plans — adds "Stories" as a togglable pseudo-platform
// so story sets can live inside a feed plan without being individually selectable per-post.
const FEED_PLATFORM_FILTERS = { ...PLATFORMS, stories: 'Stories' };
const FEED_PLATFORM_IDS = ['instagram', 'tiktok', 'ig_trial', 'linkedin', 'stories'];
const isStoryPost = (p) => Array.isArray(p.platforms) && p.platforms.includes('stories');
const sortStoriesByDate = (list) => list.slice().sort((a, b) => {
  if (!a.posting_date && !b.posting_date) return (a.position ?? 0) - (b.position ?? 0);
  if (!a.posting_date) return 1;
  if (!b.posting_date) return -1;
  if (a.posting_date === b.posting_date) return (a.position ?? 0) - (b.position ?? 0);
  return a.posting_date.localeCompare(b.posting_date);
});

const fmtShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// add N days to a 'YYYY-MM-DD' (or ISO) date, returning 'YYYY-MM-DD'
const addDays = (iso, n) => {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const emptyForm = { cover_url: '', content_link: '', pillar: '', copy: '', placeholder_text: '', platforms: ['instagram', 'tiktok'], status: 'concept', platform_status: {}, posting_date: '', platform_dates: {} };

// Upload an image File → returns the served URL (relative /api path)
const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data.url;
};

// ------- Pillar chip manager -------
const PillarsEditor = ({ client, onSave }) => {
  const [pillars, setPillars] = useState(client.pillars || []);
  const [draft, setDraft] = useState('');
  useEffect(() => { setPillars(client.pillars || []); }, [client.id, client.pillars]);

  const addPillar = async () => {
    const v = draft.trim();
    if (!v || pillars.includes(v)) { setDraft(''); return; }
    const next = [...pillars, v];
    setPillars(next);
    setDraft('');
    await onSave(next);
  };
  const removePillar = async (p) => {
    const next = pillars.filter((x) => x !== p);
    setPillars(next);
    await onSave(next);
  };

  return (
    <div className="flex items-center flex-wrap gap-1.5">
      {pillars.map((p) => (
        <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-pink-50 text-pink-700 border border-pink-100" data-testid={`pillar-chip-${p}`}>
          {p}
          <button onClick={() => removePillar(p)} className="text-pink-500 hover:text-pink-700"><X className="w-3 h-3" /></button>
        </span>
      ))}
      <div className="inline-flex items-center gap-1">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addPillar(); }}
          placeholder="add pillar"
          className="h-7 text-xs px-2 border border-slate-200 rounded-md outline-none focus:ring-2 focus:ring-pink-200 w-28"
          data-testid="pillar-input"
        />
        <button
          type="button"
          onClick={addPillar}
          className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-slate-100 text-slate-500 hover:bg-slate-200"
          data-testid="pillar-add-btn"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// ------- Feed tile -------
// content-link bubble — click opens a small inline box to view/edit the link (no full editor)
const ContentBubble = ({ post, onPatchPost }) => {
  const [draft, setDraft] = useState(post.content_link || '');
  useEffect(() => { setDraft(post.content_link || ''); }, [post.content_link]);
  const hasLink = Boolean((post.content_link || '').trim());
  const save = () => { const v = draft.trim(); if (v !== (post.content_link || '')) onPatchPost(post, { content_link: v }); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          title="content link"
          className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-full text-[9px] font-semibold shadow ring-1 ring-black/5 ${hasLink ? 'bg-sky-500 text-white hover:bg-sky-600' : 'bg-white/95 text-slate-400 hover:bg-white'}`}
          data-testid={`social-tile-content-${post.id}`}
        >
          <LinkIcon className="w-2.5 h-2.5" /> content
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" onClick={(e) => e.stopPropagation()}>
        <label className="text-[10px] uppercase tracking-wide text-slate-500">content link</label>
        <Input
          autoFocus value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
          placeholder="paste a link…"
          className="h-8 text-xs mt-1"
          data-testid={`social-tile-content-input-${post.id}`}
        />
        <div className="flex items-center gap-1.5 mt-2">
          <button type="button" disabled={!draft.trim()} onClick={() => window.open(draft.trim(), '_blank', 'noopener,noreferrer')}
            className="flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-md bg-sky-500 text-white text-xs font-medium disabled:opacity-40 hover:bg-sky-600" data-testid={`social-tile-content-open-${post.id}`}>
            <LinkIcon className="w-3 h-3" /> open
          </button>
          <button type="button" disabled={!draft.trim()} onClick={() => { copyToClipboard(draft.trim()); toast.success('link copied'); }}
            className="flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-md bg-slate-100 text-slate-700 text-xs font-medium disabled:opacity-40 hover:bg-slate-200" data-testid={`social-tile-content-copy-${post.id}`}>
            <ClipboardCopy className="w-3 h-3" /> copy
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// caption bubble — click opens a small inline box to view/edit the caption (no full editor)
const CaptionBubble = ({ post, onPatchPost }) => {
  const [draft, setDraft] = useState(post.copy || '');
  useEffect(() => { setDraft(post.copy || ''); }, [post.copy]);
  const hasCaption = Boolean((post.copy || '').trim());
  const save = () => { if ((draft || '') !== (post.copy || '')) onPatchPost(post, { copy: draft }); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          title="caption"
          className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-full text-[9px] font-semibold shadow ring-1 ring-black/5 ${hasCaption ? 'bg-pink-500 text-white hover:bg-pink-600' : 'bg-white/95 text-slate-400 hover:bg-white'}`}
          data-testid={`social-tile-caption-${post.id}`}
        >
          <Type className="w-2.5 h-2.5" /> caption
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2" onClick={(e) => e.stopPropagation()}>
        <label className="text-[10px] uppercase tracking-wide text-slate-500">caption</label>
        <Textarea
          autoFocus value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          placeholder="write a caption…"
          rows={5}
          className="text-xs mt-1 resize-none"
          data-testid={`social-tile-caption-input-${post.id}`}
        />
        <div className="flex items-center gap-1.5 mt-2">
          <button type="button" disabled={!draft.trim()} onClick={() => { copyToClipboard(draft); toast.success('caption copied'); }}
            className="flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-md bg-pink-500 text-white text-xs font-medium disabled:opacity-40 hover:bg-pink-600" data-testid={`social-tile-caption-copy-${post.id}`}>
            <ClipboardCopy className="w-3 h-3" /> copy caption
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const FeedTile = ({ post, onClick, dragProps, uploading, dropActive, onSetPlatforms, onSetStatus, onSetPlatformStatus, onSetDate, onPatchPost, clientSwatch, activePlatforms, hideMeta }) => {
  const has = Boolean(post.cover_url);
  const status = statusMap[post.status] || statusMap.draft;
  const postPlatforms = (post.platforms && post.platforms.length) ? post.platforms : ['instagram'];
  // Only show status/date for the platform(s) currently in view: if the filter is a single
  // platform, just that one's bubble; if multiple are in view, a bubble per in-view platform.
  const feedActive = (activePlatforms || []).filter((p) => p !== 'stories');
  // single-platform view → always that one platform's bubble (even if the post doesn't list it);
  // multiple in view → a bubble per in-view platform the post is on.
  let viewPlatforms;
  if (feedActive.length === 1) {
    viewPlatforms = feedActive;
  } else {
    viewPlatforms = postPlatforms.filter((pl) => feedActive.includes(pl));
    if (viewPlatforms.length === 0) viewPlatforms = postPlatforms;
  }
  const perPlatformStatus = !isStoryPost(post) && viewPlatforms.length > 1;
  // a tile with no date for the platform(s) in view is flagged red — "add a date shortly".
  // In single-platform view we only care about THAT platform's date (never other platforms'
  // dates or the generic posting_date); legacy posts with no per-platform dates fall back to
  // posting_date so they aren't wrongly flagged.
  const needsDate = !hideMeta && !isStoryPost(post)
    && viewPlatforms.every((pl) => {
      const pd = post.platform_dates || {};
      if (pd[pl]) return false;
      if (Object.keys(pd).length === 0) return !post.posting_date;
      return true;
    });
  const swatch = clientSwatch || stickyColorFor('lilac');

  const togglePlatformIn = (id) => {
    const has = postPlatforms.includes(id);
    if (has && postPlatforms.length === 1) return; // always keep at least one selected
    const next = has ? postPlatforms.filter((p) => p !== id) : [...postPlatforms, id];
    onSetPlatforms(post, next);
  };

  return (
    <div
      className={`relative aspect-[4/5] bg-slate-100 rounded-md overflow-hidden cursor-pointer group ring-1 transition-all ${dropActive ? 'ring-2 ring-pink-500 scale-[0.98]' : needsDate ? 'ring-2 ring-red-400 hover:ring-red-500' : 'ring-slate-200 hover:ring-pink-300'}`}
      onClick={onClick}
      data-testid={`social-tile-${post.id}`}
      {...dragProps}
    >
      {has ? (
        <img src={post.cover_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      ) : (
        <div
          className="w-full h-full flex flex-col items-center justify-center text-center gap-2 p-4"
          style={{ background: swatch.bg }}
          data-testid={`social-tile-placeholder-${post.id}`}
        >
          {post.placeholder_text ? (
            <span className="text-base sm:text-lg font-bold leading-snug line-clamp-5 break-words" style={{ color: swatch.ink }}>{post.placeholder_text}</span>
          ) : (
            <Type className="w-6 h-6 opacity-40" style={{ color: swatch.ink }} />
          )}
          <span className="text-[9px] uppercase tracking-widest opacity-70" style={{ color: swatch.ink }}>{post.placeholder_text ? 'placeholder · drop image' : 'tap to add text or drop image'}</span>
        </div>
      )}
      {uploading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-pink-500 animate-spin" />
        </div>
      )}
      {dropActive && (
        <div className="absolute inset-0 bg-pink-500/20 flex items-center justify-center pointer-events-none">
          <Upload className="w-6 h-6 text-pink-600" />
        </div>
      )}
      {/* pillar tag on hover */}
      {post.pillar && (
        <div className="absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="text-[10px] text-white/90 truncate text-right">{post.pillar}</div>
        </div>
      )}
      {/* platform dropdown bubble — top-left (hidden in the "All" overview) */}
      {!hideMeta && (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            title="select instagram and/or tiktok"
            className="absolute top-1.5 left-1.5 inline-flex flex-row items-center gap-1 h-5 px-1.5 rounded-full text-[9px] font-bold bg-white/95 text-slate-700 shadow ring-1 ring-black/5 hover:bg-white whitespace-nowrap"
            data-testid={`social-tile-platform-${post.id}`}
          >
            {postPlatforms.map((pl, i) => (
              <React.Fragment key={pl}>
                {i > 0 && <span className="text-slate-300">·</span>}
                <span>{PLATFORM_BADGE[pl] || pl}</span>
              </React.Fragment>
            ))}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-36 p-1.5" onClick={(e) => e.stopPropagation()}>
          {Object.entries(PLATFORMS).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => togglePlatformIn(id)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-slate-100 text-left"
              data-testid={`social-tile-platform-option-${post.id}-${id}`}
            >
              <span className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${postPlatforms.includes(id) ? 'bg-pink-500 border-pink-500' : 'border-slate-300'}`}>
                {postPlatforms.includes(id) && <Check className="w-3 h-3 text-white" />}
              </span>
              {label} · {id}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      )}
      {/* content link + caption quick bubbles — bottom-left. content sits above caption. */}
      <div className="absolute bottom-1.5 left-1.5 flex flex-col items-start gap-1">
        <ContentBubble post={post} onPatchPost={onPatchPost} />
        <CaptionBubble post={post} onPatchPost={onPatchPost} />
      </div>
      {/* per-platform (or single) status + live date — one bubble line each, top-right (shown in all views) */}
      <div className="absolute top-1.5 right-1.5 flex flex-col items-end gap-1">
        {perPlatformStatus ? (
          viewPlatforms.map((pl) => {
            const st = statusMap[platformStatusOf(post, pl)] || statusMap.draft;
            const d = (post.platform_dates && post.platform_dates[pl]) || null;
            return (
              <TileStatusPill
                key={pl}
                status={st}
                label={`${PLATFORM_BADGE[pl] || pl} ${st.label}`}
                date={d}
                onSelect={(s) => onSetPlatformStatus(post, pl, s)}
                onSetDate={(v) => onSetDate(post, pl, v)}
                testid={`social-tile-status-${post.id}-${pl}`}
              />
            );
          })
        ) : (
          (() => {
            const pl = viewPlatforms[0];
            const st = statusMap[platformStatusOf(post, pl)] || statusMap.draft;
            const pd = post.platform_dates || {};
            // only this platform's date — never another platform's posting_date (legacy posts
            // with no per-platform dates fall back to the generic date)
            const d = pd[pl] || (Object.keys(pd).length === 0 ? post.posting_date : null) || null;
            return (
              <TileStatusPill
                status={st}
                label={st.label}
                date={d}
                onSelect={(s) => onSetPlatformStatus(post, pl, s)}
                onSetDate={(v) => onSetDate(post, pl, v)}
                testid={`social-tile-status-${post.id}`}
              />
            );
          })()
        )}
      </div>
    </div>
  );
};

// ------- Post dialog -------
const PostDialog = ({ open, onOpenChange, post, pillars, clientId, onAutoSave, onDelete, plans = [], currentPlanId, onMovePlan, onCopyPlan }) => {
  const [form, setForm] = useState(emptyForm);
  const [checklist, setChecklist] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const fileRef = useRef(null);
  const contentRef = useRef(null);
  useEffect(() => {
    setChecklist(post?.checklist_items || []);
    if (post) setForm({
      cover_url: post.cover_url || '',
      content_link: post.content_link || '',
      pillar: post.pillar || '',
      copy: post.copy || '',
      placeholder_text: post.placeholder_text || '',
      platforms: (post.platforms && post.platforms.length) ? post.platforms : ['instagram'],
      status: post.status || 'draft',
      platform_status: post.platform_status || {},
      posting_date: post.posting_date ? String(post.posting_date).slice(0, 10) : '',
      platform_dates: post.platform_dates || {},
    });
    else setForm(emptyForm);
  }, [post?.id, open]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Debounced auto-save (fires ~500ms after you stop typing) so captions / content links
  // persist reliably even if the field never loses focus before the dialog closes.
  const saveTimers = useRef({});
  const debouncedAutoSave = (field, value) => {
    clearTimeout(saveTimers.current[field]);
    saveTimers.current[field] = setTimeout(() => onAutoSave({ [field]: value }), 500);
  };
  useEffect(() => () => { Object.values(saveTimers.current).forEach(clearTimeout); }, []);

  // Per-platform date override — also refreshes the single legacy posting_date to the
  // earliest set date, so calendar / public-share views (which read posting_date) stay sane.
  const writePlatformDates = (nextDates) => {
    const earliest = form.platforms.map((p) => nextDates[p]).filter(Boolean).sort()[0] || '';
    setForm((s) => ({ ...s, platform_dates: nextDates, posting_date: earliest }));
    onAutoSave({ platform_dates: nextDates, posting_date: earliest });
  };
  // Live update of just the platform being typed in — no cross-platform copy here, so a
  // half-typed year never gets propagated to the other platforms.
  const updatePlatformDate = (pl, val) => {
    writePlatformDates({ ...(form.platform_dates || {}), [pl]: val });
  };
  // On blur (a complete date) fill in any still-blank platforms with this one's date —
  // never overwrites a platform that already has its own date.
  const fillBlankPlatformDates = (pl, val) => {
    const prev = form.platform_dates || {};
    const next = { ...prev, [pl]: val };
    if (val) form.platforms.forEach((p) => { if (p !== pl && !prev[p]) next[p] = val; });
    writePlatformDates(next);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('only image files are supported'); return; }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      set('cover_url', url);
      onAutoSave({ cover_url: url });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'upload failed');
    } finally {
      setUploading(false);
    }
  };
  const onDrop = async (e) => {
    e.preventDefault();
    setDropActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await handleFile(file);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={contentRef}
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        onPointerDownOutside={(e) => {
          // Mobile keyboards (e.g. the emoji-key toolbar) sometimes fire a pointerdown that Radix
          // sees as "outside" the dialog even though it's really the on-screen keyboard rendering
          // above the field — that was closing this dialog. Only actually close for real outside taps.
          if (!contentRef.current) return;
          const r = contentRef.current.getBoundingClientRect();
          const { clientX, clientY } = e.detail.originalEvent;
          if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>post details</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Preview */}
          <div>
            <div
              className={`aspect-[4/5] w-full max-w-[260px] mx-auto md:mx-0 bg-slate-100 rounded-lg overflow-hidden ring-1 flex items-center justify-center relative cursor-pointer transition-all ${dropActive ? 'ring-2 ring-pink-500' : 'ring-slate-200 hover:ring-pink-300'}`}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); if (Array.from(e.dataTransfer.types || []).includes('Files')) setDropActive(true); }}
              onDragLeave={() => setDropActive(false)}
              onDrop={onDrop}
              data-testid="social-form-cover-drop"
            >
              {form.cover_url ? (
                <img
                  src={form.cover_url}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
                />
              ) : (
                <div className="text-slate-400 text-xs text-center px-4 flex flex-col items-center gap-2">
                  <Upload className="w-6 h-6" />
                  <span>drop an image here — or click to browse</span>
                  <span className="text-[10px] text-slate-300">or paste a URL below</span>
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-pink-500 animate-spin" />
                </div>
              )}
              {dropActive && (
                <div className="absolute inset-0 bg-pink-500/20 flex items-center justify-center pointer-events-none">
                  <Upload className="w-8 h-8 text-pink-600" />
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleFile(f); }}
                data-testid="social-form-cover-file"
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
              <span>4:5 crop preview</span>
              {form.content_link && (
                <a href={form.content_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-pink-600" onClick={(e) => e.stopPropagation()}>
                  <LinkIcon className="w-3 h-3" /> open content link
                </a>
              )}
            </div>
          </div>

          {/* Fields */}
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-slate-500">cover image url</label>
              <Input
                value={form.cover_url}
                onChange={(e) => set('cover_url', e.target.value)}
                onBlur={(e) => onAutoSave({ cover_url: e.target.value })}
                placeholder="https://…"
                className="mt-1"
                data-testid="social-form-cover"
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-500">content link (drive, ig post, etc)</label>
              <Input
                value={form.content_link}
                onChange={(e) => { set('content_link', e.target.value); debouncedAutoSave('content_link', e.target.value); }}
                onBlur={(e) => onAutoSave({ content_link: e.target.value })}
                placeholder="https://…"
                className="mt-1"
                data-testid="social-form-content-link"
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-500">placeholder note (shown as the tile until a real image is added)</label>
              <Textarea
                value={form.placeholder_text}
                onChange={(e) => { set('placeholder_text', e.target.value); debouncedAutoSave('placeholder_text', e.target.value); }}
                onBlur={(e) => onAutoSave({ placeholder_text: e.target.value })}
                placeholder="e.g. GRWM video — waiting on edit"
                rows={2}
                className="mt-1"
                data-testid="social-form-placeholder-text"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-500">pillar</label>
                <Select value={form.pillar || '__none__'} onValueChange={(v) => { const val = v === '__none__' ? '' : v; set('pillar', val); onAutoSave({ pillar: val }); }}>
                  <SelectTrigger className="mt-1" data-testid="social-form-pillar"><SelectValue placeholder="— none —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__"><span className="text-slate-400">— none —</span></SelectItem>
                    {(pillars || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                {(!pillars || pillars.length === 0) && (
                  <p className="text-[10px] text-slate-400 mt-1">no pillars set — add them above the feed</p>
                )}
              </div>
              <div>
                <label className="text-[11px] text-slate-500">platform</label>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  {Object.entries(PLATFORMS).map(([id, label]) => {
                    const active = (form.platforms || []).includes(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => {
                          const cur = form.platforms || [];
                          if (active && cur.length === 1) return;
                          if (active) {
                            const nextPlatforms = cur.filter((p) => p !== id);
                            set('platforms', nextPlatforms);
                            onAutoSave({ platforms: nextPlatforms });
                            return;
                          }
                          // adding "Trial" later auto-matches TikTok's date (if set) so it
                          // doesn't start off blank next to an already-dated platform
                          const nextDates = { ...(form.platform_dates || {}) };
                          if (id === 'ig_trial' && !nextDates.ig_trial) {
                            nextDates.ig_trial = nextDates.tiktok || form.posting_date || '';
                          }
                          const nextPlatforms = [...cur, id];
                          setForm((s) => ({ ...s, platforms: nextPlatforms, platform_dates: nextDates }));
                          onAutoSave({ platforms: nextPlatforms, platform_dates: nextDates });
                        }}
                        className={`h-9 px-3 rounded-md text-xs font-semibold border transition-colors ${active ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-500 border-slate-200 hover:border-pink-300'}`}
                        data-testid={`social-form-platform-${id}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div>
              <label className="text-[11px] text-slate-500">status{form.platforms.length > 1 ? ' (per platform)' : ''}</label>
              {form.platforms.length > 1 ? (
                <div className="mt-1 space-y-1.5">
                  <div className="flex items-center gap-2 pb-1.5 mb-1 border-b border-dashed border-slate-200">
                    <span className="text-[10px] font-semibold text-pink-600 w-10 shrink-0 uppercase">both</span>
                    <Select
                      value="__all__"
                      onValueChange={(v) => {
                        if (v === '__all__') return;
                        const next = {};
                        form.platforms.forEach((pl) => { next[pl] = v; });
                        setForm((s) => ({ ...s, platform_status: next }));
                        onAutoSave({ platform_status: next });
                      }}
                    >
                      <SelectTrigger className="flex-1" data-testid="social-form-status-all"><SelectValue>set both platforms at once…</SelectValue></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            <span className="inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
                              {s.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.platforms.map((pl) => {
                    const cur = (form.platform_status && form.platform_status[pl]) || form.status || 'concept';
                    return (
                      <div key={pl} className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold text-slate-500 w-10 shrink-0 uppercase">{PLATFORMS[pl] || pl}</span>
                        <Select
                          value={cur}
                          onValueChange={(v) => {
                            const next = { ...(form.platform_status || {}), [pl]: v };
                            setForm((s) => ({ ...s, platform_status: next }));
                            onAutoSave({ platform_status: next });
                          }}
                        >
                          <SelectTrigger className="flex-1" data-testid={`social-form-status-${pl}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => (
                              <SelectItem key={s.id} value={s.id}>
                                <span className="inline-flex items-center gap-1.5">
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
                                  {s.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Select value={form.status} onValueChange={(v) => { set('status', v); onAutoSave({ status: v }); }}>
                  <SelectTrigger className="mt-1" data-testid="social-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
                          {s.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <label className="text-[11px] text-slate-500">posting date{form.platforms.length > 1 ? ' (per platform)' : ''}</label>
              {form.platforms.length > 1 ? (
                <div className="mt-1 space-y-1.5">
                  {form.platforms.map((pl) => (
                    <div key={pl} className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold text-slate-500 w-10 shrink-0 uppercase">{PLATFORMS[pl] || pl}</span>
                      <DateInput
                        value={(form.platform_dates && form.platform_dates[pl]) || ''}
                        onChange={(v) => updatePlatformDate(pl, v)}
                        onCommit={(v) => fillBlankPlatformDates(pl, v)}
                        className="w-auto"
                        data-testid={`social-form-posting-date-${pl}`}
                      />
                      {form.platform_dates && form.platform_dates[pl] && (
                        <button
                          type="button"
                          onClick={() => updatePlatformDate(pl, '')}
                          className="text-[11px] text-slate-400 hover:text-red-600"
                          data-testid={`social-form-posting-date-clear-${pl}`}
                        >
                          clear
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-1 flex items-center gap-2">
                  <DateInput
                    value={form.posting_date || ''}
                    onChange={(v) => set('posting_date', v)}
                    onCommit={(v) => onAutoSave({ posting_date: v })}
                    className="w-auto"
                    data-testid="social-form-posting-date"
                  />
                  {form.posting_date && (
                    <button
                      type="button"
                      onClick={() => { set('posting_date', ''); onAutoSave({ posting_date: '' }); }}
                      className="text-[11px] text-slate-400 hover:text-red-600"
                      data-testid="social-form-posting-date-clear"
                    >
                      clear
                    </button>
                  )}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[11px] text-slate-500">copy</label>
                <button
                  type="button"
                  onClick={async () => { const ok = await copyToClipboard(form.copy || ''); if (ok) toast.success('copy copied to clipboard'); else toast.error('nothing to copy yet'); }}
                  className="inline-flex items-center gap-1 text-[11px] text-pink-600 hover:text-pink-700 font-medium"
                  data-testid="social-form-copy-clipboard-btn"
                ><ClipboardCopy className="w-3 h-3" /> copy</button>
              </div>
              <Textarea
                value={form.copy}
                onChange={(e) => { set('copy', e.target.value); debouncedAutoSave('copy', e.target.value); }}
                onBlur={(e) => onAutoSave({ copy: e.target.value })}
                rows={4}
                placeholder="caption / body copy…"
                className="mt-1 text-sm"
                data-testid="social-form-copy"
              />
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100">
          <label className="text-[11px] uppercase tracking-widest font-semibold text-lilac-600">feed actions · notes</label>
          <p className="text-[10px] text-slate-400 mb-1.5">shared with the right-hand feed actions panel — add in either place</p>
          <ChecklistEditor
            items={checklist}
            onChange={(next) => { setChecklist(next); onAutoSave({ checklist_items: next }); }}
            testPrefix={`post-dialog-checklist-${post?.id}`}
          />
        </div>

        <DialogFooter className="mt-2 pt-4 border-t border-slate-100 flex items-center justify-between gap-2 sm:justify-between">
          {post && (
            <button
              type="button"
              onClick={() => { if (window.confirm('delete this post?')) onDelete(post.id); }}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-red-600 hover:bg-red-50 text-sm"
              data-testid="social-form-delete"
            >
              <Trash2 className="w-4 h-4" /> delete
            </button>
          )}
          {post && plans.filter((p) => p.id !== currentPlanId).length > 0 && (
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-[11px] text-slate-500 whitespace-nowrap">move to plan</span>
              <Select value="" onValueChange={(v) => onMovePlan?.(post.id, v)}>
                <SelectTrigger className="h-9 w-40 text-xs" data-testid="social-form-move-plan"><SelectValue placeholder="choose plan…" /></SelectTrigger>
                <SelectContent>
                  {plans.filter((p) => p.id !== currentPlanId).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[11px] text-slate-500 whitespace-nowrap">copy to plan</span>
              <Select value="" onValueChange={(v) => onCopyPlan?.(post.id, v)}>
                <SelectTrigger className="h-9 w-40 text-xs" data-testid="social-form-copy-plan"><SelectValue placeholder="choose plan…" /></SelectTrigger>
                <SelectContent>
                  {plans.filter((p) => p.id !== currentPlanId).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Normalizes a story frame to {url, text} — supports legacy plain-string frames too
const normFrame = (f) => {
  if (typeof f === 'string') return { url: f, text: '' };
  if (f && typeof f === 'object') return { url: f.url || '', text: f.text || '' };
  return { url: '', text: '' };
};

// ------- Story set row (list item) -------
const StorySetRow = ({ post, onClick, dragProps }) => {
  const status = statusMap[post.status] || statusMap.concept;
  const frames = (post.story_frames || []).map(normFrame);
  return (
    <div
      className="flex flex-col gap-2 bg-white border border-slate-200 rounded-xl p-3 cursor-pointer hover:border-pink-300 transition-colors"
      onClick={onClick}
      data-testid={`social-story-row-${post.id}`}
      {...dragProps}
    >
      <span className={`text-sm font-semibold truncate ${post.title ? 'text-slate-800' : 'text-slate-400 italic font-normal'}`} data-testid={`social-story-title-${post.id}`}>
        {post.title || 'untitled story set'}
      </span>
      <div className="flex items-center gap-3">
      <div className="flex items-center gap-1.5 overflow-x-auto">
        {frames.length === 0 ? (
          <div className="w-[134px] h-[240px] rounded-md bg-lilac-50 border border-dashed border-lilac-200 flex items-center justify-center shrink-0">
            <Type className="w-6 h-6 text-lilac-400" />
          </div>
        ) : (
          frames.map((f, i) => (
            <div key={i} className="w-[134px] h-[240px] rounded-md overflow-hidden shrink-0 ring-1 ring-slate-200">
              {f.url ? (
                <img src={f.url} alt="" className="w-full h-full object-cover" />
              ) : f.text ? (
                <div className="w-full h-full bg-lilac-50 flex items-center justify-center p-2.5">
                  <span className="text-xs leading-tight text-lilac-600 font-medium text-center line-clamp-6 break-words">{f.text}</span>
                </div>
              ) : (
                <div className="w-full h-full bg-lilac-50 flex items-center justify-center"><Type className="w-5 h-5 text-lilac-300" /></div>
              )}
            </div>
          ))
        )}
      </div>
      <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500">{frames.length} frame{frames.length === 1 ? '' : 's'}</span>
          {post.content_link && <LinkIcon className="w-3 h-3 text-pink-400" title="drive link added" />}
          {post.copy && <span className="text-[10px] text-lilac-500" title="has notes">notes</span>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {post.posting_date && (
            <span className="inline-flex items-center h-6 px-2 rounded-full text-[10px] font-semibold bg-slate-50 text-slate-600 ring-1 ring-slate-200" data-testid={`social-story-date-${post.id}`}>
              {fmtShortDate(post.posting_date)}
            </span>
          )}
          <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-semibold bg-slate-50 text-slate-700 ring-1 ring-slate-200" data-testid={`social-story-status-${post.id}`}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.dot }} /> {status.label}
          </span>
        </div>
      </div>
      </div>
    </div>
  );
};

// ------- Story set dialog -------
const StorySetDialog = ({ open, onOpenChange, post, onAutoSave, onDelete }) => {
  const [title, setTitle] = useState('');
  const [frames, setFrames] = useState([]);
  const [status, setStatus] = useState('concept');
  const [postingDate, setPostingDate] = useState('');
  const [contentLink, setContentLink] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const addFileRef = useRef(null);
  const replaceFileRef = useRef(null);
  const replaceIndexRef = useRef(null);
  const dragIndexRef = useRef(null);

  useEffect(() => {
    if (post) {
      setTitle(post.title || '');
      setFrames((post.story_frames || []).map(normFrame));
      setStatus(post.status || 'concept');
      setPostingDate(post.posting_date ? String(post.posting_date).slice(0, 10) : '');
      setContentLink(post.content_link || '');
      setNotes(post.copy || '');
    }
  }, [post?.id, open]);

  // Upload one or more images at once — fills blank/text-only slots first (image always
  // takes display priority over a frame's text, same rule as feed placeholders), then appends
  const addFrames = async (files) => {
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    if (!imageFiles.length) { toast.error('only image files are supported'); return; }
    setUploading(true);
    try {
      const urls = await Promise.all(imageFiles.map((f) => uploadImage(f)));
      const next = frames.map((f) => ({ ...f }));
      let ui = 0;
      for (let i = 0; i < next.length && ui < urls.length; i++) {
        if (!next[i].url) { next[i].url = urls[ui]; ui += 1; }
      }
      while (ui < urls.length) { next.push({ url: urls[ui], text: '' }); ui += 1; }
      setFrames(next);
      onAutoSave({ story_frames: next });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'upload failed');
    } finally {
      setUploading(false);
    }
  };
  const addPlaceholderFrame = () => {
    const next = [...frames, { url: '', text: '' }];
    setFrames(next);
    onAutoSave({ story_frames: next });
  };
  const replaceFrameAt = async (idx, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('only image files are supported'); return; }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      const next = frames.map((f, i) => i === idx ? { ...f, url } : f);
      setFrames(next);
      onAutoSave({ story_frames: next });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'upload failed');
    } finally {
      setUploading(false);
    }
  };
  const removeFrame = (idx) => {
    const next = frames.filter((_, i) => i !== idx);
    setFrames(next);
    onAutoSave({ story_frames: next });
  };
  const setFrameText = (idx, text) => {
    setFrames((prev) => prev.map((f, i) => i === idx ? { ...f, text } : f));
  };
  const commitFrames = (next) => onAutoSave({ story_frames: next ?? frames });

  // Drag-to-reorder frames within the story set
  const onFrameDragStart = (i) => (e) => { dragIndexRef.current = i; e.dataTransfer.effectAllowed = 'move'; };
  const onFrameDragOver = (i) => (e) => { e.preventDefault(); setDragOverIdx(i); };
  const onFrameDragLeave = (i) => () => { if (dragOverIdx === i) setDragOverIdx(null); };
  const onFrameDrop = (i) => (e) => {
    e.preventDefault();
    setDragOverIdx(null);
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    if (from === null || from === i) return;
    const next = [...frames];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setFrames(next);
    onAutoSave({ story_frames: next });
  };

  if (!post) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>story set</DialogTitle>
        </DialogHeader>

        <div>
          <label className="text-[11px] text-slate-500">name / heading</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={(e) => onAutoSave({ title: e.target.value })}
            placeholder="e.g. GRWM story, launch teaser…"
            className="mt-1"
            data-testid="story-form-title"
          />
        </div>

        <div className="mt-3">
          <label className="text-[11px] text-slate-500">frames (9:16, shown in order) — drag to reorder · click an image to swap it</label>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {frames.map((f, i) => (
              <div
                key={i}
                draggable
                onDragStart={onFrameDragStart(i)}
                onDragOver={onFrameDragOver(i)}
                onDragLeave={onFrameDragLeave(i)}
                onDrop={onFrameDrop(i)}
                className={`relative w-24 aspect-[9/16] rounded-lg overflow-hidden ring-1 group transition-all cursor-grab active:cursor-grabbing ${dragOverIdx === i ? 'ring-2 ring-pink-500 scale-[0.97]' : 'ring-slate-200'}`}
                data-testid={`story-frame-${post.id}-${i}`}
              >
                {f.url ? (
                  <img
                    src={f.url}
                    alt=""
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => { replaceIndexRef.current = i; replaceFileRef.current?.click(); }}
                  />
                ) : (
                  <Textarea
                    value={f.text}
                    onChange={(e) => setFrameText(i, e.target.value)}
                    onBlur={() => commitFrames()}
                    placeholder="tap to add text"
                    className="w-full h-full resize-none border-0 rounded-none bg-lilac-50 text-[10px] text-lilac-700 font-medium text-center placeholder:text-lilac-400 focus-visible:ring-1 focus-visible:ring-pink-300 p-2"
                    data-testid={`story-frame-text-${post.id}-${i}`}
                  />
                )}
                <span className="absolute top-1 left-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-black/60 text-white text-[9px] font-semibold pointer-events-none">{i + 1}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); replaceIndexRef.current = i; replaceFileRef.current?.click(); }}
                  title={f.url ? 'swap this frame\'s image' : 'add image to this frame'}
                  className={`absolute bottom-1 left-1 w-5 h-5 inline-flex items-center justify-center rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity ${f.url ? 'bg-black/60 hover:bg-black/80' : 'bg-white/90 text-lilac-600 hover:bg-white'}`}
                  data-testid={`story-frame-upload-${post.id}-${i}`}
                >
                  <Upload className="w-3 h-3" />
                </button>
                {!f.url && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); commitFrames(); }}
                    title="save this frame's text"
                    className="absolute bottom-1 right-1 w-5 h-5 inline-flex items-center justify-center rounded-full bg-white/90 text-lilac-600 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`story-frame-text-save-${post.id}-${i}`}
                  >
                    <Check className="w-3 h-3" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeFrame(i); }}
                  className="absolute top-1 right-1 w-5 h-5 inline-flex items-center justify-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`story-frame-remove-${post.id}-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {(
              <button
                type="button"
                onClick={() => addFileRef.current?.click()}
                disabled={uploading}
                className="w-24 aspect-[9/16] rounded-lg border border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-pink-600 hover:border-pink-300 transition-colors"
                data-testid={`story-frame-add-${post.id}`}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                <span className="text-[10px] text-center">add frame(s)</span>
              </button>
            )}
            {(
              <button
                type="button"
                onClick={addPlaceholderFrame}
                className="w-24 aspect-[9/16] rounded-lg border border-dashed border-lilac-300 flex flex-col items-center justify-center gap-1 text-lilac-400 hover:text-lilac-600 hover:border-lilac-400 transition-colors"
                data-testid={`story-frame-add-placeholder-${post.id}`}
              >
                <Type className="w-4 h-4" />
                <span className="text-[10px] text-center">add placeholder</span>
              </button>
            )}
            <input
              ref={addFileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ''; if (files.length) addFrames(files); }}
              data-testid={`story-frame-file-${post.id}`}
            />
            <input
              ref={replaceFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f && replaceIndexRef.current !== null) replaceFrameAt(replaceIndexRef.current, f); }}
              data-testid={`story-frame-replace-file-${post.id}`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="text-[11px] text-slate-500">status</label>
            <Select value={status} onValueChange={(v) => { setStatus(v); onAutoSave({ status: v }); }}>
              <SelectTrigger className="mt-1" data-testid="story-form-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
                      {s.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-slate-500">date</label>
            <DateInput
              value={postingDate}
              onChange={(v) => setPostingDate(v)}
              onCommit={(v) => onAutoSave({ posting_date: v })}
              className="mt-1"
              data-testid="story-form-posting-date"
            />
          </div>
        </div>

        <div className="mt-3">
          <label className="text-[11px] text-slate-500">drive / content link</label>
          <Input
            value={contentLink}
            onChange={(e) => setContentLink(e.target.value)}
            onBlur={(e) => onAutoSave({ content_link: e.target.value })}
            placeholder="https://…"
            className="mt-1"
            data-testid="story-form-content-link"
          />
        </div>

        <div className="mt-3">
          <label className="text-[11px] text-slate-500">notes</label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={(e) => onAutoSave({ copy: e.target.value })}
            placeholder="notes for this story set…"
            rows={3}
            className="mt-1 text-sm"
            data-testid="story-form-notes"
          />
        </div>

        <DialogFooter className="mt-2 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={() => { if (window.confirm('delete this story set?')) onDelete(post.id); }}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-red-600 hover:bg-red-50 text-sm"
            data-testid="story-form-delete"
          >
            <Trash2 className="w-4 h-4" /> delete
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ------- Content drive link (per-plan) -------
const KeyMessageBox = ({ value, onSave }) => {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  return (
    <div className="bg-pink-50 border border-pink-200 rounded-xl p-4 mb-4" data-testid="social-key-message">
      <div className="flex items-center gap-2 mb-1.5">
        <Type className="w-4 h-4 text-pink-500" />
        <span className="text-[11px] uppercase tracking-widest text-pink-600 font-semibold">key message</span>
      </div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { if ((draft || '') !== (value || '')) onSave(draft.trim()); }}
        rows={2}
        placeholder="add a key message for this plan… (also shows on the share link)"
        className="w-full bg-transparent text-sm text-slate-700 outline-none resize-none placeholder:text-pink-300/80"
        data-testid="social-key-message-input"
      />
    </div>
  );
};

const ContentDriveLink = ({ url, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(url || '');
  useEffect(() => { setDraft(url || ''); }, [url]);

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); onChange(draft.trim()); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { setEditing(false); onChange(draft.trim()); }
          if (e.key === 'Escape') { setEditing(false); setDraft(url || ''); }
        }}
        placeholder="paste drive link…"
        className="h-8 text-xs px-2 border border-pink-300 rounded-md outline-none focus:ring-2 focus:ring-pink-200 w-56"
        data-testid="social-content-drive-input"
      />
    );
  }

  if (url) {
    return (
      <span className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium bg-pink-50 border border-pink-200 text-pink-700">
        <LinkIcon className="w-3.5 h-3.5 shrink-0" />
        <a
          href={url.startsWith('http') ? url : `https://${url}`}
          target="_blank"
          rel="noreferrer"
          className="hover:underline"
          data-testid="social-content-drive-open"
        >content drive</a>
        <button type="button" onClick={() => setEditing(true)} className="text-pink-400 hover:text-pink-700" title="edit link" data-testid="social-content-drive-edit"><Pencil className="w-3 h-3" /></button>
        <button type="button" onClick={() => onChange('')} className="text-pink-400 hover:text-red-600" title="remove link" data-testid="social-content-drive-remove"><X className="w-3 h-3" /></button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium border border-dashed border-slate-300 text-slate-500 hover:text-pink-600 hover:border-pink-300"
      data-testid="social-content-drive-add"
    >
      <LinkIcon className="w-3.5 h-3.5" /> content drive
    </button>
  );
};

// ------- Shared checklist / notes editor (used in Feed Actions panel + post dialog) -------
const newChecklistId = () => 'chk_' + (window.crypto?.randomUUID ? window.crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2, 6));
const ChecklistEditor = ({ items, onChange, testPrefix }) => {
  const [draft, setDraft] = useState('');
  const addItem = () => {
    const text = draft.trim();
    if (!text) return;
    onChange([...items, { id: newChecklistId(), text, done: false }]);
    setDraft('');
  };
  const toggleItem = (id) => onChange(items.map((i) => i.id === id ? { ...i, done: !i.done } : i));
  const removeItem = (id) => onChange(items.filter((i) => i.id !== id));
  return (
    <div onClick={(e) => e.stopPropagation()}>
      {items.length > 0 && (
        <ul className="space-y-1 mb-1.5">
          {items.map((i) => (
            <li key={i.id} className="flex items-start gap-1.5 group" data-testid={`${testPrefix}-item-${i.id}`}>
              <input
                type="checkbox"
                checked={Boolean(i.done)}
                onChange={() => toggleItem(i.id)}
                className="mt-0.5 w-3 h-3 accent-lilac-500 cursor-pointer shrink-0"
                data-testid={`${testPrefix}-done-${i.id}`}
              />
              <span className={`flex-1 min-w-0 text-[11px] text-slate-600 break-words ${i.done ? 'line-through opacity-50' : ''}`}>{i.text}</span>
              <button
                type="button"
                onClick={() => removeItem(i.id)}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 shrink-0"
                data-testid={`${testPrefix}-delete-${i.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-1">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
          placeholder="+ add note…"
          className="h-6 text-[11px] px-1.5 border border-lilac-200 rounded-md outline-none focus:ring-2 focus:ring-lilac-300 flex-1 bg-white"
          data-testid={`${testPrefix}-input`}
        />
        <button
          type="button"
          onClick={addItem}
          className="h-6 w-6 inline-flex items-center justify-center rounded-md bg-lilac-100 text-lilac-600 hover:bg-lilac-200 shrink-0"
          data-testid={`${testPrefix}-add`}
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// ------- Per-post checklist (tick-list) — Feed Actions only, never shown on the feed tile -------
const PostChecklist = ({ post, onPatch }) => {
  const [open, setOpen] = useState(false);
  const items = post.checklist_items || [];
  const doneCount = items.filter((i) => i.done).length;

  return (
    <div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="text-[10px] text-lilac-500 hover:text-lilac-700 font-medium shrink-0 inline-flex items-center gap-0.5"
        data-testid={`feed-actions-checklist-toggle-${post.id}`}
      >
        {items.length > 0 ? `${doneCount}/${items.length}` : '+ notes'}
      </button>
      {open && (
        <div className="mt-1.5 pl-1 border-l-2 border-lilac-200">
          <ChecklistEditor
            items={items}
            onChange={(next) => onPatch(post, next)}
            testPrefix={`feed-actions-checklist-${post.id}`}
          />
        </div>
      )}
    </div>
  );
};

// ------- Feed Actions panel (right column) -------
// small box above Feed Actions — jump to today + open the calendar view
const FeedToolsBox = ({ onToday, onOpenCalendar }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="feed-tools-box">
    <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">jump + view</div>
    <div className="flex items-center gap-2">
      <button onClick={onToday} className="flex-1 inline-flex items-center justify-center gap-1 h-9 rounded-lg bg-pink-500 text-white text-xs font-semibold hover:bg-pink-600 transition-colors" data-testid="feed-today-btn">
        <CalendarDays className="w-3.5 h-3.5" /> today
      </button>
      <button onClick={onOpenCalendar} className="flex-1 inline-flex items-center justify-center gap-1 h-9 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold hover:bg-slate-200 transition-colors" data-testid="feed-calendar-btn">
        <CalendarDays className="w-3.5 h-3.5" /> calendar view
      </button>
    </div>
  </div>
);

// diary-style calendar of the current feed — posts sit on their live date (for the platform in
// view). Optional from/to date filter. Click a post to open it.
const CAL_WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const FeedCalendarDialog = ({ open, onOpenChange, posts, dateOf, onOpenPost }) => {
  const [cursor, setCursor] = useState(new Date());
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const monthLabel = cursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const byDate = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      const d = dateOf(p);
      if (!d) return;
      if (from && d < from) return;
      if (to && d > to) return;
      (map[d] = map[d] || []).push(p);
    });
    return map;
  }, [posts, from, to, dateOf]);
  const first = new Date(y, m, 1);
  const startWeekday = (first.getDay() + 6) % 7; // Mon = 0
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const iso = (d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const todayISO = new Date().toLocaleDateString('en-CA');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>feed calendar</DialogTitle></DialogHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <button onClick={() => setCursor(new Date(y, m - 1, 1))} className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-100" data-testid="feed-cal-prev">‹</button>
            <span className="text-sm font-semibold w-36 text-center">{monthLabel}</span>
            <button onClick={() => setCursor(new Date(y, m + 1, 1))} className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-100" data-testid="feed-cal-next">›</button>
            <button onClick={() => setCursor(new Date())} className="ml-1 text-xs font-semibold text-pink-600 hover:text-pink-700" data-testid="feed-cal-today">today</button>
          </div>
          <div className="flex items-center gap-1.5 text-[11px]">
            <span className="text-slate-400">dates</span>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-[140px] text-xs" data-testid="feed-cal-from" />
            <span className="text-slate-400">→</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-[140px] text-xs" data-testid="feed-cal-to" />
            {(from || to) && <button onClick={() => { setFrom(''); setTo(''); }} className="text-slate-400 hover:text-red-600" title="clear filter" data-testid="feed-cal-clear"><X className="w-3.5 h-3.5" /></button>}
          </div>
        </div>
        <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden mt-2">
          {CAL_WEEKDAYS.map((w) => <div key={w} className="bg-slate-50 text-[10px] uppercase text-slate-500 font-semibold text-center py-1">{w}</div>)}
          {cells.map((d, i) => (
            <div key={i} className={`min-h-[84px] p-1 ${d === null ? 'bg-slate-50' : 'bg-white'}`}>
              {d !== null && (
                <>
                  <div className={`text-[10px] font-semibold ${iso(d) === todayISO ? 'text-pink-600' : 'text-slate-400'}`}>{d}</div>
                  <div className="mt-0.5 space-y-0.5">
                    {(byDate[iso(d)] || []).map((p) => {
                      const st = statusMap[p.status] || statusMap.draft;
                      return (
                        <button key={p.id} onClick={() => { onOpenPost(p); onOpenChange(false); }} className="w-full flex items-center gap-1 text-left text-[9px] px-1 py-0.5 rounded bg-slate-50 hover:bg-pink-50 transition-colors" data-testid={`feed-cal-post-${p.id}`}>
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: st.dot }} />
                          <span className="truncate">{p.placeholder_text || p.copy || 'post'}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 mt-1">showing live dates for the platform you're viewing · click a post to open it</p>
      </DialogContent>
    </Dialog>
  );
};


const MemberTagControl = ({ post, team, onPatchPost }) => {
  const tags = post.tags || [];
  const toggle = (uid) => {
    const next = tags.includes(uid) ? tags.filter((t) => t !== uid) : [...tags, uid];
    onPatchPost(post, { tags: next });
  };
  const initials = (name) => (name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const tagged = team.filter((u) => tags.includes(u.user_id || u.id));
  return (
    <div className="mt-1 flex items-center gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
      {tagged.map((u) => (
        <span key={u.user_id || u.id} className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-mint-100 text-mint-700 text-[9px] font-bold" title={u.name} data-testid={`feed-actions-tag-${post.id}-${u.user_id || u.id}`}>{initials(u.name)}</span>
      ))}
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-slate-100 text-slate-500 text-[9px] font-semibold hover:bg-slate-200" data-testid={`feed-actions-tag-btn-${post.id}`}>
            <AtSign className="w-2.5 h-2.5" /> tag
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-48 p-1 max-h-64 overflow-y-auto">
          {team.length === 0 ? (
            <div className="text-[11px] text-slate-400 px-2 py-1.5">no team members</div>
          ) : team.map((u) => {
            const uid = u.user_id || u.id;
            return (
              <button key={uid} type="button" onClick={() => toggle(uid)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-slate-100 text-left" data-testid={`feed-actions-tag-opt-${post.id}-${uid}`}>
                <span className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${tags.includes(uid) ? 'bg-mint-500 border-mint-500' : 'border-slate-300'}`}>
                  {tags.includes(uid) && <Check className="w-3 h-3 text-white" />}
                </span>
                <span className="truncate">{u.name}</span>
              </button>
            );
          })}
        </PopoverContent>
      </Popover>
    </div>
  );
};

const FeedActionsPanel = ({ conceptPosts, archivedPosts = [], onOpenPost, onPatchChecklist, team = [], onPatchPost }) => {
  const [personFilter, setPersonFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const byPerson = (list) => personFilter === 'all' ? list : list.filter((p) => (p.tags || []).includes(personFilter));
  const activeList = byPerson(conceptPosts);
  const archivedList = byPerson(archivedPosts);
  const list = showArchived ? archivedList : activeList;
  const initials = (name) => (name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="w-96 shrink-0 bg-white border border-slate-200 rounded-xl p-3" data-testid="feed-actions-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Feed Actions</div>
        <button
          onClick={() => setShowArchived((v) => !v)}
          className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-semibold transition-colors ${showArchived ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
          data-testid="feed-actions-archive-toggle"
        >
          <Archive className="w-2.5 h-2.5" /> {showArchived ? 'back to active' : `archived (${archivedPosts.length})`}
        </button>
      </div>

      {/* filter by the person tagged with the action */}
      <div className="flex items-center gap-1.5 mb-2" data-testid="feed-actions-person-filter">
        <AtSign className="w-3 h-3 text-slate-400 shrink-0" />
        <Select value={personFilter} onValueChange={setPersonFilter}>
          <SelectTrigger className="h-7 text-[11px]"><SelectValue placeholder="anyone with an action" /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">anyone with an action</SelectItem>
            {team.map((u) => {
              const uid = u.user_id || u.id;
              return <SelectItem key={uid} value={uid}>{u.name}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>

      <div className="text-[10px] uppercase tracking-wide text-lilac-600 font-semibold mb-1.5">{showArchived ? 'archived · done' : 'to plan · concept'}</div>
      {list.length === 0 ? (
        <div className="text-xs text-slate-400 italic">{showArchived ? 'nothing archived' : (personFilter === 'all' ? 'no concept posts' : 'no actions for this person')}</div>
      ) : (
        <ul className="space-y-1.5">
          {list.map((p) => (
            <li key={p.id} className="relative px-2 py-1.5 rounded-lg bg-lilac-50 border border-lilac-200" data-testid={`feed-actions-concept-item-${p.id}`}>
              {showArchived ? (
                <button
                  type="button"
                  onClick={() => onPatchPost(p, { action_done: false })}
                  title="restore this action to the active list"
                  className="absolute top-1.5 right-1.5 w-4 h-4 rounded border border-slate-300 bg-mint-500 hover:bg-white flex items-center justify-center group/done"
                  data-testid={`feed-actions-restore-${p.id}`}
                >
                  <Check className="w-3 h-3 text-white group-hover/done:text-mint-600" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onPatchPost(p, { action_done: true })}
                  title="mark this action done (moves it to archived)"
                  className="absolute top-1.5 right-1.5 w-4 h-4 rounded border border-lilac-300 bg-white hover:bg-mint-500 hover:border-mint-500 flex items-center justify-center group/done"
                  data-testid={`feed-actions-done-${p.id}`}
                >
                  <Check className="w-3 h-3 text-transparent group-hover/done:text-white" />
                </button>
              )}
              <button
                type="button"
                onClick={() => onOpenPost(p)}
                className="w-full flex items-center gap-2 text-left pr-5"
              >
                {p.cover_url ? (
                  <img src={p.cover_url} alt="" className="w-7 h-7 rounded object-cover shrink-0" />
                ) : (
                  <span className="w-7 h-7 rounded bg-lilac-100 flex items-center justify-center shrink-0"><Type className="w-3.5 h-3.5 text-lilac-500" /></span>
                )}
                <span className="flex-1 min-w-0 text-xs text-slate-700 truncate">{p.placeholder_text || p.pillar || 'untitled concept'}</span>
                {p.posting_date && <span className="text-[10px] text-lilac-700 font-medium shrink-0">{fmtShortDate(p.posting_date)}</span>}
              </button>
              <div className="mt-1 pl-9">
                <PostChecklist post={p} onPatch={onPatchChecklist} />
                <MemberTagControl post={p} team={team} onPatchPost={onPatchPost} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ------- Main page -------
const Social = () => {
  const { user } = useAuth();
  const canAccess = ['manager', 'talent_manager'].includes(user?.role) || user?.role === 'social_manager' || user?.social_access;

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(() => {
    try { return localStorage.getItem('social.clientId') || ''; } catch { return ''; }
  });
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState('');
  const [posts, setPosts] = useState([]);
  const [team, setTeam] = useState([]);  // team members available to tag on feed actions
  useEffect(() => { api.get('/users').then(({ data }) => setTeam(data || [])).catch(() => {}); }, []);
  // only people who can actually access the Social page can be tagged / filtered on feed actions
  const socialTeam = useMemo(() => team.filter((u) => ['manager', 'social_manager'].includes(u.role) || u.social_access), [team]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pillarFilter, setPillarFilter] = useState('all');
  const [platformView, setPlatformView] = useState('all');  // 'all' | single platform id
  const platformFilter = useMemo(() => platformView === 'all' ? FEED_PLATFORM_IDS : [platformView], [platformView]);
  const isAllView = platformView === 'all';
  // The date that drives feed order. Single-platform view uses ONLY that platform's date — never
  // another platform's date or the generic posting_date — so a post left blank for this platform
  // is treated as undated (sits at the top, red outline) instead of inheriting e.g. IG's date.
  // Legacy posts with no per-platform dates at all fall back to posting_date. All view = IG order.
  const feedPrimary = isAllView ? 'instagram' : platformFilter[0];
  const feedDateOf = (p) => {
    const pd = (p && p.platform_dates) || {};
    if (feedPrimary && pd[feedPrimary]) return pd[feedPrimary];
    if (isAllView) return (p && p.posting_date) || '';
    if (Object.keys(pd).length === 0) return (p && p.posting_date) || '';
    return '';
  };
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [dropOverId, setDropOverId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);  // tile briefly highlighted after "today"
  const [calendarOpen, setCalendarOpen] = useState(false);
  const tileRefs = useRef({});
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileRef = useRef(null);
  const [renamingPlan, setRenamingPlan] = useState(false);
  const [planNameDraft, setPlanNameDraft] = useState('');
  const [newPlanOpen, setNewPlanOpen] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanMonth, setNewPlanMonth] = useState('');
  const [newPlanType, setNewPlanType] = useState('feed');
  const [newPlanDaysPerWeek, setNewPlanDaysPerWeek] = useState(5);

  const client = useMemo(() => clients.find((c) => c.id === clientId) || null, [clients, clientId]);
  const plan = useMemo(() => plans.find((p) => p.id === planId) || null, [plans, planId]);
  const clientSwatch = useMemo(() => stickyColorFor(clientColorFor(client)), [client]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/clients');
        setClients(data || []);
        if (!clientId && data?.length) setClientId(data[0].id);
      } catch (e) { toast.error('failed to load clients'); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load plans whenever client changes
  useEffect(() => {
    if (!clientId) { setPlans([]); setPlanId(''); setPosts([]); return; }
    try { localStorage.setItem('social.clientId', clientId); } catch {}
    (async () => {
      try {
        const { data } = await api.get('/social/plans', { params: { client_id: clientId } });
        const list = data || [];
        setPlans(list);
        // restore last-used plan for this client, or fall back to first plan (or none)
        let restored = '';
        try { restored = localStorage.getItem(`social.planId.${clientId}`) || ''; } catch {}
        if (restored && list.some((p) => p.id === restored)) setPlanId(restored);
        else setPlanId(list[0]?.id || '');
      } catch (e) { toast.error('failed to load plans'); }
    })();
  }, [clientId]);

  // Load posts whenever the selected plan changes
  useEffect(() => {
    if (!clientId) { setPosts([]); return; }
    if (!planId) { setPosts([]); return; }
    try { localStorage.setItem(`social.planId.${clientId}`, planId); } catch {}
    (async () => {
      try {
        const { data } = await api.get('/social/posts', { params: { client_id: clientId, plan_id: planId } });
        setPosts(data || []);
      } catch (e) { toast.error('failed to load posts'); }
    })();
  }, [clientId, planId]);

  const createPlan = async (name, month, type, daysPerWeek) => {
    const clean = (name || '').trim() || `Plan ${plans.length + 1}`;
    try {
      const { data } = await api.post('/social/plans', {
        client_id: clientId,
        name: clean,
        type: type || 'feed',
        month: (type === 'stories' ? undefined : (month || undefined)),
        days_per_week: (type === 'stories' ? undefined : (month ? daysPerWeek : undefined)),
      });
      setPlans((ps) => [...ps, data]);
      setPlanId(data.id);
      toast.success(type === 'stories' ? 'stories plan created' : (month ? `plan created — ${daysPerWeek}× a week pre-filled across ${month}` : 'plan created'));
    } catch { toast.error('failed to create plan'); }
  };
  const renamePlan = async (id, name) => {
    const clean = (name || '').trim();
    if (!clean) { setRenamingPlan(false); return; }
    try {
      const { data } = await api.patch(`/social/plans/${id}`, { name: clean });
      setPlans((ps) => ps.map((p) => p.id === id ? (data || { ...p, name: clean }) : p));
      setRenamingPlan(false);
    } catch { toast.error('rename failed'); }
  };
  const deletePlan = async (id) => {
    if (!window.confirm('delete this plan and all its posts?')) return;
    try {
      await api.delete(`/social/plans/${id}`);
      const remaining = plans.filter((p) => p.id !== id);
      setPlans(remaining);
      setPlanId(remaining[0]?.id || '');
      toast.success('plan deleted');
    } catch { toast.error('delete failed'); }
  };

  const sharePlan = async (id) => {
    try {
      const { data } = await api.post(`/social/plans/${id}/share`);
      const url = `${window.location.origin}/share/social/${data.token}`;
      const copied = await copyToClipboard(url);
      if (copied) toast.success('view-only link copied — no login needed');
      else toast.success('view-only link ready', { description: url, duration: 15000 });
    } catch { toast.error('failed to create share link'); }
  };

  const savePillars = async (next) => {
    if (!client) return;
    try {
      const { data } = await api.patch(`/clients/${client.id}`, { pillars: next });
      setClients((cs) => cs.map((c) => c.id === client.id ? { ...c, pillars: data?.pillars ?? next } : c));
    } catch { toast.error('failed to update pillars'); }
  };

  const saveContentDriveLink = async (link) => {
    if (!plan) return;
    try {
      const { data } = await api.patch(`/social/plans/${plan.id}`, { content_drive_link: link });
      setPlans((ps) => ps.map((p) => p.id === plan.id ? (data || { ...p, content_drive_link: link }) : p));
    } catch { toast.error('failed to save content drive link'); }
  };

  const saveKeyMessage = async (msg) => {
    if (!plan) return;
    try {
      const { data } = await api.patch(`/social/plans/${plan.id}`, { key_message: msg });
      setPlans((ps) => ps.map((p) => p.id === plan.id ? (data || { ...p, key_message: msg }) : p));
    } catch { toast.error('failed to save key message'); }
  };

  const openNew = async () => {
    if (!planId) { toast.error('create a plan first'); return; }
    try {
      const { data } = await api.post('/social/posts', { client_id: clientId, plan_id: planId });
      setPosts((ps) => [data, ...ps]);
      setEditing(data);
      setDialogOpen(true);
    } catch { toast.error('failed to create post'); }
  };
  const openNewStorySet = async () => {
    if (!planId) { toast.error('create a plan first'); return; }
    try {
      const { data } = await api.post('/social/posts', { client_id: clientId, plan_id: planId, story_frames: [], platforms: ['stories'] });
      setPosts((ps) => [data, ...ps]);
      setEditing(data);
      setDialogOpen(true);
    } catch { toast.error('failed to create story set'); }
  };
  const openEdit = (p) => { setEditing(p); setDialogOpen(true); };

  const setPostPlatforms = async (post, platforms) => {
    try {
      const { data } = await api.patch(`/social/posts/${post.id}`, { platforms });
      setPosts((ps) => ps.map((p) => p.id === post.id ? data : p));
    } catch { toast.error('failed to update platform'); }
  };

  const setPostStatus = async (post, status) => {
    try {
      const { data } = await api.patch(`/social/posts/${post.id}`, { status });
      setPosts((ps) => ps.map((p) => p.id === post.id ? data : p));
    } catch { toast.error('failed to update status'); }
  };

  const setPostPlatformStatus = async (post, pl, status) => {
    const next = { ...(post.platform_status || {}), [pl]: status };
    try {
      const { data } = await api.patch(`/social/posts/${post.id}`, { platform_status: next });
      setPosts((ps) => ps.map((p) => p.id === post.id ? data : p));
    } catch { toast.error('failed to update status'); }
  };

  const setPostPlatformDate = async (post, pl, val) => {
    const next = { ...(post.platform_dates || {}) };
    if (val) next[pl] = val; else delete next[pl];
    const earliest = (post.platforms || []).map((p) => next[p]).filter(Boolean).sort()[0] || '';
    try {
      const { data } = await api.patch(`/social/posts/${post.id}`, { platform_dates: next, posting_date: earliest });
      setPosts((ps) => ps.map((p) => p.id === post.id ? data : p));
    } catch { toast.error('failed to update date'); }
  };

  const togglePlatformFilter = (id) => {
    setPlatformView(id);
  };

  const handleBulkUpload = async (files) => {
    if (!planId || !files?.length) return;
    setBulkUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        urls.push(await uploadImage(file));
      }
      if (!urls.length) { toast.error('no valid images selected'); return; }
      const { data } = await api.post('/social/posts/bulk', { client_id: clientId, plan_id: planId, cover_urls: urls });
      setPosts((ps) => [...data, ...ps]);
      toast.success(`${data.length} tile${data.length > 1 ? 's' : ''} added`);
    } catch { toast.error('bulk upload failed'); }
    finally { setBulkUploading(false); }
  };

  const addPlaceholder = async () => {
    if (!planId) { toast.error('create a plan first'); return; }
    const text = window.prompt('placeholder note for this tile?');
    if (text === null || !text.trim()) return;
    try {
      const { data } = await api.post('/social/posts', { client_id: clientId, plan_id: planId, placeholder_text: text.trim() });
      setPosts((ps) => [data, ...ps]);
      toast.success('placeholder added');
    } catch { toast.error('failed to add placeholder'); }
  };

  // Every field in the post dialog auto-saves on change/blur — no separate save button.
  const autoSavePost = async (patch) => {
    if (!editing) return;
    try {
      const { data } = await api.patch(`/social/posts/${editing.id}`, patch);
      setPosts((ps) => ps.map((p) => p.id === editing.id ? data : p));
      setEditing(data);
    } catch { toast.error('save failed'); }
  };
  // quick inline tile edits (content link / caption) — patch a single post directly
  const patchPost = async (post, patch) => {
    try {
      const { data } = await api.patch(`/social/posts/${post.id}`, patch);
      setPosts((ps) => ps.map((p) => p.id === post.id ? data : p));
    } catch { toast.error('save failed'); }
  };
  const deletePost = async (id) => {
    try {
      await api.delete(`/social/posts/${id}`);
      setPosts((ps) => ps.filter((p) => p.id !== id));
      setDialogOpen(false);
      toast.success('post deleted');
    } catch { toast.error('delete failed'); }
  };
  const movePostToPlan = async (id, targetPlanId) => {
    if (!targetPlanId || targetPlanId === planId) return;
    try {
      await api.patch(`/social/posts/${id}`, { plan_id: targetPlanId });
      setPosts((ps) => ps.filter((p) => p.id !== id));
      setDialogOpen(false);
      const target = plans.find((p) => p.id === targetPlanId);
      toast.success(`moved to ${target?.name || 'plan'}`);
    } catch { toast.error('failed to move post'); }
  };
  const copyPostToPlan = async (id, targetPlanId) => {
    if (!targetPlanId || targetPlanId === planId) return;
    try {
      await api.post(`/social/posts/${id}/copy`, null, { params: { target_plan_id: targetPlanId } });
      const target = plans.find((p) => p.id === targetPlanId);
      toast.success(`copied to ${target?.name || 'plan'}`, { description: 'the original stays in this plan' });
    } catch { toast.error('failed to copy post'); }
  };

  // Drag-to-reorder (internal) + drop-file-to-set-cover (external)
  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const onDragStart = (id) => (e) => { setDragId(id); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    if (isFileDrag(e)) { e.dataTransfer.dropEffect = 'copy'; setDropOverId(id); }
    else { e.dataTransfer.dropEffect = 'move'; }
  };
  const onDragLeave = (id) => (e) => {
    if (dropOverId === id) setDropOverId(null);
  };
  const setPostCover = async (post, url) => {
    try {
      const { data } = await api.patch(`/social/posts/${post.id}`, { cover_url: url });
      setPosts((ps) => ps.map((p) => p.id === post.id ? data : p));
    } catch { toast.error('failed to set cover'); }
  };
  const onDrop = (overId) => async (e) => {
    e.preventDefault();
    // External file drop → upload as cover
    if (isFileDrag(e)) {
      setDropOverId(null);
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) { toast.error('only image files are supported'); return; }
      const post = posts.find((p) => p.id === overId);
      if (!post) return;
      setUploadingId(overId);
      try {
        const url = await uploadImage(file);
        await setPostCover(post, url);
        toast.success('cover uploaded');
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'upload failed');
      } finally {
        setUploadingId(null);
      }
      return;
    }
    // Internal drag → drop the post exactly where placed. The feed is date-ordered, so moving
    // a post to a *different* date slot means its date no longer matches where it sits: clear
    // the date (for the in-view platform/s) and flag it red until a new date is added. Moving a
    // post next to another post that shares its date is just a same-day reorder → keep the date.
    if (!dragId || dragId === overId) return;
    if (isAllView) { setDragId(null); return; }  // reordering only in a single-platform view
    const draggedId = dragId;
    setDragId(null);
    const dragged = posts.find((p) => p.id === draggedId);
    if (!dragged) return;

    // Work in the on-screen feed order so the post lands exactly where the user drops it.
    const displayed = feedGridTiles.map((p) => p.id);
    const from = displayed.indexOf(draggedId);
    const to = displayed.indexOf(overId);
    if (from < 0 || to < 0) return;
    const nextIds = displayed.slice();
    nextIds.splice(from, 1);
    nextIds.splice(to, 0, draggedId);
    const posMap = Object.fromEntries(nextIds.map((id, i) => [id, i]));

    setPosts((ps) => ps.map((p) => posMap[p.id] !== undefined ? { ...p, position: posMap[p.id] } : p));

    // same-day check: does the dropped post share a date with a new neighbour? (uses the in-view
    // platform's date only — see feedDateOf)
    const draggedDate = feedDateOf(dragged);
    const idx = nextIds.indexOf(draggedId);
    const prevP = idx > 0 ? posts.find((p) => p.id === nextIds[idx - 1]) : null;
    const nextP = idx < nextIds.length - 1 ? posts.find((p) => p.id === nextIds[idx + 1]) : null;
    const sameDay = draggedDate && ((prevP && feedDateOf(prevP) === draggedDate) || (nextP && feedDateOf(nextP) === draggedDate));

    try {
      await api.post('/social/posts/reorder', { client_id: clientId, plan_id: planId, ordered_ids: nextIds });
    } catch { toast.error('could not move post'); }
    // only a DATED post dropped onto a different date gets cleared (→ red, holds its new slot);
    // an already-undated (red) post just repositions, and same-day moves keep their date.
    if (draggedDate && !sameDay) clearDatesFor(draggedId);
  };

  // clear a dragged post's date(s): only the in-view platform when one is selected, else all.
  const clearDatesFor = async (id) => {
    if (!id) return;
    const post = posts.find((p) => p.id === id);
    const feedPlatforms = platformFilter.filter((p) => p !== 'stories');
    let patch;
    if (feedPlatforms.length === 1) {
      const pl = feedPlatforms[0];
      const nextDates = { ...(post?.platform_dates || {}) };
      delete nextDates[pl];
      const earliest = (post?.platforms || []).map((p) => nextDates[p]).filter(Boolean).sort()[0] || '';
      patch = { platform_dates: nextDates, posting_date: earliest };
    } else {
      patch = { posting_date: '', platform_dates: {} };
    }
    setPosts((ps) => ps.map((p) => p.id === id ? { ...p, ...patch } : p));
    try { await api.patch(`/social/posts/${id}`, patch); } catch { toast.error('could not clear dates'); }
  };

  // FEED ORDER — the calendar date is the #1 priority: dated posts run latest-at-top down to
  // earliest-at-bottom, using ONLY the in-view platform's date (see feedDateOf; All view = IG).
  // A post left blank for the platform in view is red and simply stays in the manual slot it was
  // dragged to — you can drop it anywhere and it holds there until you give it a date, at which
  // point the date hierarchy takes over and re-places it.
  const filtered = useMemo(() => {
    const base = posts.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (pillarFilter !== 'all' && (p.pillar || '') !== pillarFilter) return false;
      const postPlatforms = (p.platforms && p.platforms.length) ? p.platforms : ['instagram'];
      if (!postPlatforms.some((pl) => platformFilter.includes(pl))) return false;
      return true;
    });
    const byPos = base.slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const datedSorted = byPos.filter((p) => feedDateOf(p)).sort((a, b) => {
      const c = feedDateOf(b).localeCompare(feedDateOf(a));
      return c !== 0 ? c : (a.position ?? 0) - (b.position ?? 0);
    });
    let k = 0;
    return byPos.map((p) => (feedDateOf(p) ? datedSorted[k++] : p));
  }, [posts, statusFilter, pillarFilter, platformFilter, isAllView]);

  // Story sets list (dedicated stories-type plan) — sorted so the soonest date is at the top;
  // undated sets sink to the bottom (position order among ties), no platform filtering.
  const visibleStorySets = useMemo(() => {
    return sortStoriesByDate(posts.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (pillarFilter !== 'all' && (p.pillar || '') !== pillarFilter) return false;
      return true;
    }));
  }, [posts, statusFilter, pillarFilter]);

  // Feed-type plan: split the platform+status+pillar-filtered set into normal grid tiles vs.
  // story-set rows (platforms=['stories']) — the story rows always sort soonest-date-first,
  // same as the dedicated stories view, just placed differently (see onlyStoriesSelected below).
  const feedGridTiles = useMemo(() => filtered.filter((p) => !isStoryPost(p)), [filtered]);

  // "today" button → scroll to the post dated today (or nearest dated post) and flash it
  const scrollToToday = () => {
    const todayISO = new Date().toLocaleDateString('en-CA');
    const dated = feedGridTiles.filter((p) => feedDateOf(p));
    if (!dated.length) { toast.message('no dated posts to jump to'); return; }
    let target = dated.find((p) => feedDateOf(p) === todayISO);
    if (!target) {
      target = dated.reduce((best, p) => {
        const diff = Math.abs(new Date(feedDateOf(p)).getTime() - new Date(todayISO).getTime());
        return (!best || diff < best.diff) ? { p, diff } : best;
      }, null)?.p;
    }
    if (target && tileRefs.current[target.id]) {
      tileRefs.current[target.id].scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightId(target.id);
    }
  };
  // clear the highlight on the next click anywhere
  useEffect(() => {
    if (!highlightId) return;
    const clear = () => setHighlightId(null);
    const t = setTimeout(() => document.addEventListener('click', clear, { once: true }), 60);
    return () => { clearTimeout(t); document.removeEventListener('click', clear); };
  }, [highlightId]);
  const feedStoryRows = useMemo(() => sortStoriesByDate(filtered.filter(isStoryPost)), [filtered]);
  const onlyStoriesSelected = platformFilter.length === 1 && platformFilter[0] === 'stories';

  const counts = useMemo(() => {
    // Reflects the platform + pillar filters (not status, since this breakdown *is* by status)
    // so picking a single platform updates the numbers to that platform's posts only.
    const scoped = posts.filter((p) => {
      if (pillarFilter !== 'all' && (p.pillar || '') !== pillarFilter) return false;
      const postPlatforms = (p.platforms && p.platforms.length) ? p.platforms : ['instagram'];
      return postPlatforms.some((pl) => platformFilter.includes(pl));
    });
    const c = { total: scoped.length, concept: 0, draft: 0, ready: 0, scheduled: 0, live: 0 };
    scoped.forEach((p) => { c[p.status] = (c[p.status] || 0) + 1; });
    return c;
  }, [posts, pillarFilter, platformFilter]);

  // Feed Actions: concept posts (soonest posting_date first, no-date last), each with its own
  // Feed Actions tick-list (checklist_items) — never shown on the feed preview tile.
  const conceptPosts = useMemo(() => {
    const DONE = ['ready', 'scheduled', 'live'];
    const isActioned = (p) => p.action_done || DONE.includes(p.status) || Object.values(p.platform_status || {}).some((s) => DONE.includes(s));
    return posts
      .filter((p) => p.status === 'concept')
      // drop once it's ready/scheduled/live on any platform, or ticked done by hand
      .filter((p) => !isActioned(p))
      // only surface a feed action if something's actually been written/added to it
      .filter((p) => (p.placeholder_text || '').trim() || (p.copy || '').trim() || (p.pillar || '').trim() || (p.checklist_items || []).length > 0 || p.cover_url)
      .slice()
      .sort((a, b) => {
        if (!a.posting_date && !b.posting_date) return 0;
        if (!a.posting_date) return 1;
        if (!b.posting_date) return -1;
        return a.posting_date.localeCompare(b.posting_date);
      });
  }, [posts]);
  // archived = concept posts ticked done by hand (kept so they can be reviewed / restored)
  const archivedConceptPosts = useMemo(() => posts.filter((p) => p.status === 'concept' && p.action_done), [posts]);

  const patchChecklist = async (post, items) => {
    setPosts((ps) => ps.map((p) => p.id === post.id ? { ...p, checklist_items: items } : p));
    try {
      const { data } = await api.patch(`/social/posts/${post.id}`, { checklist_items: items });
      setPosts((ps) => ps.map((p) => p.id === post.id ? data : p));
    } catch { toast.error('failed to save note'); }
  };

  if (!canAccess) {
    return (
      <div className="p-8 text-sm text-slate-500">access denied</div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">social</h1>
          <p className="text-xs text-slate-500 mt-1">plan a client's instagram feed — cover, pillar, copy, status</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="min-w-[220px]" data-testid="social-client-select"><SelectValue placeholder="pick a client" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <button
            onClick={openNew}
            disabled={!clientId || !planId || plan?.type === 'stories'}
            className={`inline-flex items-center gap-1.5 px-3 h-10 rounded-md bg-pink-500 text-white hover:bg-pink-600 text-sm font-medium disabled:opacity-40 ${plan?.type === 'stories' ? 'hidden' : ''}`}
            data-testid="social-add-post"
          >
            <Plus className="w-4 h-4" /> new post
          </button>
          <button
            onClick={() => bulkFileRef.current?.click()}
            disabled={!clientId || !planId || bulkUploading}
            className={`inline-flex items-center gap-1.5 px-3 h-10 rounded-md bg-white border border-slate-200 text-slate-700 hover:border-pink-300 hover:text-pink-600 text-sm font-medium disabled:opacity-40 ${plan?.type === 'stories' ? 'hidden' : ''}`}
            data-testid="social-bulk-upload-btn"
          >
            {bulkUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Images className="w-4 h-4" />} bulk upload
          </button>
          <input
            ref={bulkFileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ''; if (files.length) handleBulkUpload(files); }}
            data-testid="social-bulk-upload-input"
          />
          <button
            onClick={addPlaceholder}
            disabled={!clientId || !planId}
            className={`inline-flex items-center gap-1.5 px-3 h-10 rounded-md bg-white border border-slate-200 text-slate-700 hover:border-lilac-400 hover:text-lilac-700 text-sm font-medium disabled:opacity-40 ${plan?.type === 'stories' ? 'hidden' : ''}`}
            data-testid="social-add-placeholder"
          >
            <Type className="w-4 h-4" /> placeholder
          </button>
          {(
            <button
              onClick={openNewStorySet}
              disabled={!clientId || !planId}
              className="inline-flex items-center gap-1.5 px-3 h-10 rounded-md bg-pink-500 text-white hover:bg-pink-600 text-sm font-medium disabled:opacity-40"
              data-testid="social-add-story-set"
            >
              <Plus className="w-4 h-4" /> new story
            </button>
          )}
        </div>
      </div>

      {!clientId ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-400">select a client to plan their feed</div>
      ) : (
        <>
          {/* Plan tabs */}
          <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 mb-3 flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-wide text-slate-400 mr-1">plans</span>
            {plans.length === 0 && (
              <span className="text-xs text-slate-400 italic">no plans yet — this client doesn't need one until you create it</span>
            )}
            {plans.map((p) => (
              <div key={p.id} className="relative inline-flex items-center">
                <button
                  onClick={() => setPlanId(p.id)}
                  onDoubleClick={() => { setPlanId(p.id); setPlanNameDraft(p.name); setRenamingPlan(true); }}
                  className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium border transition-colors ${planId === p.id ? 'bg-pink-500 text-white border-pink-500 pr-7' : 'bg-white text-slate-600 border-slate-200 hover:border-pink-300'}`}
                  data-testid={`social-plan-tab-${p.id}`}
                  title="double-click to rename"
                >
                  {p.name}
                  {p.type === 'stories' && <span className={`text-[9px] opacity-80 ${planId === p.id ? 'text-white' : 'text-lilac-500'}`}>· stories</span>}
                </button>
                {planId === p.id && !renamingPlan && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setPlanNameDraft(p.name); setRenamingPlan(true); }}
                    className="absolute right-2 text-white/80 hover:text-white"
                    title="rename plan"
                    data-testid={`social-plan-rename-btn-${p.id}`}
                  ><Pencil className="w-3 h-3" /></button>
                )}
              </div>
            ))}
            {renamingPlan && plan && (
              <input
                autoFocus
                value={planNameDraft}
                onChange={(e) => setPlanNameDraft(e.target.value)}
                onBlur={() => renamePlan(plan.id, planNameDraft)}
                onKeyDown={(e) => { if (e.key === 'Enter') renamePlan(plan.id, planNameDraft); if (e.key === 'Escape') setRenamingPlan(false); }}
                className="h-8 text-xs px-2 border border-pink-300 rounded-full outline-none focus:ring-2 focus:ring-pink-200 w-32"
                data-testid="social-plan-rename-input"
              />
            )}
            <button
              onClick={() => {
                setNewPlanName(`Plan ${plans.length + 1}`);
                setNewPlanMonth(new Date().toISOString().slice(0, 7));
                setNewPlanType('feed');
                setNewPlanOpen(true);
              }}
              className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-medium border border-dashed border-slate-300 text-slate-500 hover:text-pink-600 hover:border-pink-300"
              data-testid="social-add-plan"
            >
              <Plus className="w-3.5 h-3.5" /> new plan
            </button>
            {plan && (
              <button
                onClick={() => sharePlan(plan.id)}
                className="ml-auto inline-flex items-center gap-1 px-2 h-8 rounded-md text-[11px] text-slate-500 hover:text-pink-600 hover:bg-pink-50"
                data-testid="social-share-plan"
                title="copy view-only link (no login needed)"
              >
                <Share2 className="w-3.5 h-3.5" /> share
              </button>
            )}
            {plan && (
              <button
                onClick={() => deletePlan(plan.id)}
                className="inline-flex items-center gap-1 px-2 h-8 rounded-md text-[11px] text-slate-400 hover:text-red-600 hover:bg-red-50"
                data-testid="social-delete-plan"
                title="delete current plan"
              >
                <Trash2 className="w-3.5 h-3.5" /> delete plan
              </button>
            )}
          </div>

          {plans.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-400">
              this client has no social plan yet. click <span className="font-medium text-pink-600">new plan</span> above to start one.
            </div>
          ) : (
          <>
          <KeyMessageBox value={plan?.key_message} onSave={saveKeyMessage} />
          {/* Pillars + filters */}
          <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 mb-4 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wide text-slate-400">pillars</span>
              {client && <PillarsEditor client={client} onSave={savePillars} />}
            </div>
            <ContentDriveLink url={plan?.content_drive_link} onChange={saveContentDriveLink} />
            <div className="flex-1" />
            <div className={`flex items-center gap-1 ${plan?.type === 'stories' ? 'hidden' : ''}`} data-testid="social-platform-filter">
              <span className="text-[11px] uppercase tracking-wide text-slate-400 mr-1">platform</span>
              <button
                onClick={() => setPlatformView('all')}
                className={`h-8 px-2.5 rounded-full text-xs font-semibold border transition-colors ${isAllView ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-500 border-slate-200 hover:border-pink-300'}`}
                data-testid="social-platform-filter-all"
              >
                All
              </button>
              {Object.entries(FEED_PLATFORM_FILTERS).map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setPlatformView(id)}
                  className={`h-8 px-2.5 rounded-full text-xs font-semibold border transition-colors ${platformView === id ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-500 border-slate-200 hover:border-pink-300'}`}
                  data-testid={`social-platform-filter-${id}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-400">filter</span>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 min-w-[120px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">all statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={pillarFilter} onValueChange={setPillarFilter}>
                <SelectTrigger className="h-8 min-w-[120px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">all pillars</SelectItem>
                  {(client?.pillars || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Feed (left) + Feed Actions (right) */}
          <div className="flex gap-4 items-start">
          <div className="max-w-[960px] w-full flex-1">
          {/* Feed header (IG-style handle bar) */}
          <div className="bg-white border border-slate-200 rounded-t-xl px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-mint-500 to-lilac-400 p-[2px]">
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-xs font-semibold text-slate-700">
                {(client?.name || '?').slice(0, 2).toUpperCase()}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-800">
                {client?.handle || client?.name}
                {plan && <span className="ml-2 text-[11px] font-normal text-slate-400">· {plan.name}</span>}
              </div>
              <div className="text-[11px] text-slate-500">
                {counts.total} posts · <span className="text-lilac-700">{counts.concept} concept</span> · <span className="text-slate-600">{counts.draft} draft</span> · <span className="text-amber-600">{counts.ready} ready</span> · <span className="text-lilac-700">{counts.scheduled} scheduled</span> · <span className="text-emerald-600">{counts.live} live</span>
              </div>
            </div>
          </div>

          {/* Grid */}
          <div className="bg-white border border-t-0 border-slate-200 rounded-b-xl p-2">
            {plan?.type === 'stories' ? (
              visibleStorySets.length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-400">no story sets yet — hit "new story" to start planning</div>
              ) : (
                <div className="space-y-2" data-testid="social-story-list">
                  {visibleStorySets.map((p) => (
                    <div
                      key={p.id}
                      onDragOver={onDragOver(p.id)}
                      onDragLeave={onDragLeave(p.id)}
                      onDrop={onDrop(p.id)}
                    >
                      <StorySetRow
                        post={p}
                        onClick={() => openEdit(p)}
                        dragProps={{
                          draggable: true,
                          onDragStart: onDragStart(p.id),
                        }}
                      />
                    </div>
                  ))}
                </div>
              )
            ) : (
              feedGridTiles.length === 0 && feedStoryRows.length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-400">no posts yet — hit "new post" to start planning</div>
              ) : (
                <>
                  {onlyStoriesSelected && feedStoryRows.length > 0 && (
                    <div className="space-y-2 mb-2" data-testid="social-feed-story-rows-top">
                      {feedStoryRows.map((p) => (
                        <StorySetRow key={p.id} post={p} onClick={() => openEdit(p)} />
                      ))}
                    </div>
                  )}
                  {feedGridTiles.length > 0 && (
                    <div className="grid grid-cols-3 gap-0.5 sm:gap-1">
                      {feedGridTiles.map((p) => (
                        <div
                          key={p.id}
                          ref={(el) => { tileRefs.current[p.id] = el; }}
                          className={highlightId === p.id ? 'ring-4 ring-pink-400 rounded-md ring-offset-1 transition-all' : ''}
                          onDragOver={onDragOver(p.id)}
                          onDragLeave={onDragLeave(p.id)}
                          onDrop={onDrop(p.id)}
                        >
                          <FeedTile
                            post={p}
                            onClick={() => openEdit(p)}
                            uploading={uploadingId === p.id}
                            dropActive={dropOverId === p.id}
                            onSetPlatforms={setPostPlatforms}
                            onSetStatus={setPostStatus}
                            onSetPlatformStatus={setPostPlatformStatus}
                            onSetDate={setPostPlatformDate}
                            onPatchPost={patchPost}
                            clientSwatch={clientSwatch}
                            activePlatforms={platformFilter}
                            hideMeta={isAllView}
                            dragProps={{
                              draggable: !isAllView,
                              onDragStart: isAllView ? undefined : onDragStart(p.id),
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {!onlyStoriesSelected && feedStoryRows.length > 0 && (
                    <div className={`space-y-2 ${feedGridTiles.length > 0 ? 'mt-3 pt-3 border-t border-slate-100' : ''}`} data-testid="social-feed-story-rows-bottom">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 px-1 mb-1">stories</div>
                      {feedStoryRows.map((p) => (
                        <StorySetRow key={p.id} post={p} onClick={() => openEdit(p)} />
                      ))}
                    </div>
                  )}
                </>
              )
            )}
          </div>
          <p className="mt-2 text-[10px] text-slate-400 text-right">{plan?.type === 'stories' ? 'tip: sets with a sooner date float to the top automatically · double-click a plan tab to rename' : 'tip: drop an image onto a tile to set the cover · drag tiles to reorder · story sets sit as rows below (or on top if "stories" is the only platform selected) · double-click a plan tab to rename'}</p>
          </div>
          <div className="w-96 shrink-0 flex flex-col gap-3">
            <FeedToolsBox onToday={scrollToToday} onOpenCalendar={() => setCalendarOpen(true)} />
            <FeedActionsPanel
              conceptPosts={conceptPosts}
              archivedPosts={archivedConceptPosts}
              onOpenPost={openEdit}
              onPatchChecklist={patchChecklist}
              team={socialTeam}
              onPatchPost={patchPost}
            />
          </div>
          </div>
          </>
          )}
        </>
      )}

      {(plan?.type === 'stories' || (editing && isStoryPost(editing))) ? (
        <StorySetDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          post={editing}
          onAutoSave={autoSavePost}
          onDelete={deletePost}
        />
      ) : (
        <PostDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          post={editing}
          pillars={client?.pillars || []}
          clientId={clientId}
          onAutoSave={autoSavePost}
          onDelete={deletePost}
          plans={plans}
          currentPlanId={planId}
          onMovePlan={movePostToPlan}
          onCopyPlan={copyPostToPlan}
        />
      )}

      <FeedCalendarDialog
        open={calendarOpen}
        onOpenChange={setCalendarOpen}
        posts={feedGridTiles}
        dateOf={feedDateOf}
        onOpenPost={openEdit}
      />


      <Dialog open={newPlanOpen} onOpenChange={setNewPlanOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>new plan</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-slate-500">plan type</label>
              <div className="mt-1 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setNewPlanType('feed')}
                  className={`h-9 px-3 rounded-md text-xs font-semibold border transition-colors ${newPlanType === 'feed' ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-500 border-slate-200 hover:border-pink-300'}`}
                  data-testid="new-plan-type-feed"
                >
                  feed
                </button>
                <button
                  type="button"
                  onClick={() => setNewPlanType('stories')}
                  className={`h-9 px-3 rounded-md text-xs font-semibold border transition-colors ${newPlanType === 'stories' ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-500 border-slate-200 hover:border-pink-300'}`}
                  data-testid="new-plan-type-stories"
                >
                  stories
                </button>
              </div>
            </div>
            <div>
              <label className="text-[11px] text-slate-500">plan name</label>
              <Input
                autoFocus
                value={newPlanName}
                onChange={(e) => setNewPlanName(e.target.value)}
                className="mt-1"
                data-testid="new-plan-name-input"
              />
            </div>
            {newPlanType === 'feed' && (
              <>
              <div>
                <label className="text-[11px] text-slate-500">month to pre-fill</label>
                <Input
                  type="month"
                  value={newPlanMonth}
                  onChange={(e) => setNewPlanMonth(e.target.value)}
                  className="mt-1"
                  data-testid="new-plan-month-input"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">posts per week (pre-fills the best posting days each week)</label>
                <div className="mt-1 flex items-center gap-1.5 flex-wrap" data-testid="new-plan-cadence">
                  {[3, 4, 5, 6, 7].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setNewPlanDaysPerWeek(n)}
                      className={`h-9 w-10 rounded-md text-xs font-semibold border transition-colors ${newPlanDaysPerWeek === n ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-500 border-slate-200 hover:border-pink-300'}`}
                      data-testid={`new-plan-cadence-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-[10px] text-slate-400">{newPlanDaysPerWeek === 7 ? 'a post for every day of the month' : `${newPlanDaysPerWeek} a week on the best posting days (Mon, Tue, Thu, Sat, Sun first)`}</p>
              </div>
              </>
            )}
          </div>
          <DialogFooter className="mt-2">
            <button onClick={() => setNewPlanOpen(false)} className="px-3 h-9 rounded-md text-slate-600 hover:bg-slate-100 text-sm">cancel</button>
            <button
              onClick={() => { createPlan(newPlanName, newPlanMonth, newPlanType, newPlanDaysPerWeek); setNewPlanOpen(false); }}
              className="px-4 h-9 rounded-md bg-pink-500 text-white hover:bg-pink-600 text-sm font-medium"
              data-testid="new-plan-create-btn"
            >
              create plan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Social;
